"use client";

import { Button, buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Send2 } from "iconsax-react";
import IconSupportComment from "@/assets/images/muscle-support.svg?react";

import { ReactNode, useEffect, useState } from "react";
import { createProfileSupportComments } from "@/actions/profiles";
import { Link } from "react-router-dom";
import { localizedPath } from "@/lib/localePath";
import { useSearchParams, useRouter } from "next/navigation";
import { SupportedLanguage } from "@/lib/shared";
import lang from "./lang.json";
import mainLang from "@/app/[locale]/(main)/lang.json";
import { cn } from "@/lib/utils";

type SupportCommentDialogProps = {
  userId: string;
  supportComments: number;
  isLoggedIn?: boolean;
  children?: ReactNode;
  locale?: SupportedLanguage;
  triggerIconClassName?: string;
  reelRailMode?: boolean;
};

export default function SupportCommentDialog({
  userId,
  isLoggedIn,
  children,
  locale = "en",
  supportComments,
  triggerIconClassName,
  reelRailMode = false,
}: SupportCommentDialogProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isOpen, isOpenSet] = useState(false);
  const [isSubmitting, isSubmittingSet] = useState(false);
  const [comment, commentSet] = useState("");

  const handleSubmit = async () => {
    if (isSubmitting || !comment.trim()) return;

    isSubmittingSet(true);
    try {
      const result = await createProfileSupportComments(userId, {
        comment: comment.trim(),
      });

      if (result.status === "success") {
        commentSet(""); // Clear the textarea
        // Close after slight delay to give feedback
        setTimeout(() => {
          isOpenSet(false);
        }, 300);
      } else {
        console.error("Failed to create support comment:", result.message);
      }
    } catch (error) {
      console.error("Failed to create support comment:", error);
    } finally {
      isSubmittingSet(false);
    }
  };

  useEffect(() => {
    const nextAction = searchParams.get("next");

    if (nextAction === `support-user-${userId}`) {
      isOpenSet(true);
      router.replace("?");
    }
  }, [searchParams, router, userId]);

  if (!isLoggedIn) {
    return (
        <Link
          to={localizedPath(
            locale,
            `/auth/login?redirect=/?next=support-user-${userId}`,
          )}
          className={buttonVariants({
            variant: "ghost",
            size: "icon-lg",
            class: "flex flex-col svg items-center justify-center gap-1 h-auto",
          })}
        >
          <span
            className={cn(
              "rounded-full p-2",
              reelRailMode ? "" : "bg-gray-200",
            )}
          >
          <IconSupportComment
            className={cn(
              "fill-current",
              reelRailMode ? "text-white" : "text-gray-900",
              triggerIconClassName,
            )}
          />
        </span>
        <span
          className={cn(
            supportComments > 0 ? "text-sm" : "text-xs",
            reelRailMode && "text-white",
          )}
        >
          {supportComments > 0 ? supportComments : mainLang["action-support"][locale]}
        </span>
      </Link>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={isOpenSet}>
      <DialogTrigger asChild>
        {children || (
          <button
            type="button"
            className="flex flex-col cursor-pointer items-center justify-center gap-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
          <span
            className={cn(
              "w-10 rounded-full p-2",
              reelRailMode ? "" : "bg-gray-200",
            )}
          >
              <IconSupportComment
                className={cn(
                  "fill-current",
                  reelRailMode ? "text-white" : "text-gray-900",
                  triggerIconClassName,
                )}
              />
            </span>
            <span
              className={cn(
                supportComments > 0 ? "text-sm" : "text-xs",
                reelRailMode && "text-white",
              )}
            >
              {supportComments > 0 ? supportComments : mainLang["action-support"][locale]}
            </span>
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{lang["add-support-comment"][locale]}</DialogTitle>
          <DialogDescription>
            {lang["add-support-comment-description"][locale]}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Comment Textarea */}
          <div className="space-y-2">
            <Textarea
              placeholder={lang["support-comment-placeholder"][locale]}
              value={comment}
              onChange={(e) => commentSet(e.target.value)}
              className="min-h-[100px] resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button
              variant="ghost"
              onClick={() => {
                isOpenSet(false);
                commentSet(""); // Clear on cancel
              }}
              disabled={isSubmitting}
            >
              {lang["cancel"][locale]}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !comment.trim()}
            >
              <Send2
                size={16}
                className="fill-current mr-2"
                variant="Outline"
              />
              {isSubmitting ? lang["submitting"][locale] : lang["submit-comment"][locale]}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
